(() => {
  'use strict';

  const CONFIG = Object.freeze({
    version: '2026-09-28.1',
    productId: '9781012365634',
    shopDomain: 'cb170e.myshopify.com',
    perPage: 5,
    fetchPageSize: 30,
    endpoint: 'https://cdn.judge.me/reviews/reviews_for_widget',
    curatedReviewUuids: [
      'e661e69e-8036-4770-b2c5-8c479ca49d39',
      '546af892-1b5c-4234-9908-bce5c16cbd88',
      '27a98720-b128-4cf3-ba43-13e4b06137cb',
      'e451b9c9-9118-401f-9aec-0bc07179be3a',
      '657e0e5a-fc23-45a1-aed3-27808bf74b6c',
      '9d31fe4b-7e4b-45ee-81ac-89fb877646d6',
      '3198a12c-15f8-4942-b2a5-32cf92127942',
      '42593fcf-b5c1-4c60-b8ae-bdc3bb4cc452',
      '858d3291-d08e-4d2a-aac8-88e599ea51b1',
      '0d68b546-8afd-48b2-ac5d-5a377d14b9ff',
      '6f63c36b-3e1a-4050-b40e-9155f492e641',
      '67bb964d-844f-41b1-93a4-18ddbeceb9f5',
      'ac274fd4-bb3b-48ed-b82e-29c52efc09b0',
      '5a622917-ae14-4cba-a248-e5ad84e23e8c',
      '8edf7933-0cf2-4c6b-8ee7-5d768225a88c'
    ],
    naturalCuratedPositions: [0, 1, 2, 3, 4, 36, 54, 80, 97, 242, 286, 337, 402, 434, 789]
  });

  const singleReviewCache = new Map();
  const nativePageCache = new Map();
  const curatedSet = new Set(CONFIG.curatedReviewUuids);
  let widget = null;
  let currentPage = 1;
  let renderSequence = 0;
  let initialized = false;

  function endpointUrl(params) {
    const query = new URLSearchParams({
      url: CONFIG.shopDomain,
      shop_domain: CONFIG.shopDomain,
      platform: 'shopify',
      product_id: CONFIG.productId,
      ...params
    });
    return `${CONFIG.endpoint}?${query.toString()}`;
  }

  async function getJson(params) {
    const response = await fetch(endpointUrl(params), { credentials: 'omit' });
    if (!response.ok) throw new Error(`Judge.me request failed (${response.status})`);
    return response.json();
  }

  function reviewHtmlFromResponse(html) {
    const documentFragment = new DOMParser().parseFromString(html || '', 'text/html');
    return [...documentFragment.querySelectorAll('.jdgm-rev')].map((review) => review.outerHTML);
  }

  async function fetchSingleReview(uuid) {
    if (!singleReviewCache.has(uuid)) {
      singleReviewCache.set(uuid, (async () => {
        const payload = await getJson({ page: '1', per_page: String(CONFIG.perPage), review_uuid: uuid });
        const reviews = reviewHtmlFromResponse(payload.html);
        if (reviews.length !== 1) throw new Error(`Could not load curated Judge.me review ${uuid}`);
        return reviews[0];
      })());
    }
    return singleReviewCache.get(uuid);
  }

  async function fetchNativePage(page) {
    if (!nativePageCache.has(page)) {
      nativePageCache.set(page, (async () => {
        const payload = await getJson({
          page: String(page),
          per_page: String(CONFIG.fetchPageSize),
          sort_by: 'most-helpful'
        });
        return {
          totalCount: Number(payload.total_count) || 0,
          reviews: reviewHtmlFromResponse(payload.html)
        };
      })());
    }
    return nativePageCache.get(page);
  }

  function naturalPositionForNonCuratedIndex(nonCuratedIndex) {
    let naturalPosition = nonCuratedIndex;
    while (true) {
      const curatedBeforeOrAt = CONFIG.naturalCuratedPositions.filter((position) => position <= naturalPosition).length;
      const nextPosition = nonCuratedIndex + curatedBeforeOrAt;
      if (nextPosition === naturalPosition) return naturalPosition;
      naturalPosition = nextPosition;
    }
  }

  async function reviewsForCustomPage(page) {
    if (page <= 3) {
      const start = (page - 1) * CONFIG.perPage;
      const uuids = CONFIG.curatedReviewUuids.slice(start, start + CONFIG.perPage);
      return {
        totalCount: 1310,
        reviews: await Promise.all(uuids.map(fetchSingleReview))
      };
    }

    const nonCuratedStart = (page - 4) * CONFIG.perPage;
    const naturalPositions = Array.from({ length: CONFIG.perPage }, (_, index) =>
      naturalPositionForNonCuratedIndex(nonCuratedStart + index)
    );
    const nativePages = [...new Set(naturalPositions.map((position) => Math.floor(position / CONFIG.fetchPageSize) + 1))];
    const fetchedPages = new Map(await Promise.all(nativePages.map(async (nativePage) => [nativePage, await fetchNativePage(nativePage)])));
    const reviews = naturalPositions.map((position) => {
      const nativePage = Math.floor(position / CONFIG.fetchPageSize) + 1;
      const pageIndex = position % CONFIG.fetchPageSize;
      return fetchedPages.get(nativePage)?.reviews[pageIndex];
    }).filter(Boolean);
    const totalCount = fetchedPages.values().next().value?.totalCount || 1310;
    return { totalCount, reviews };
  }

  function isCustomOrderActive() {
    if (!widget) return false;
    const sort = widget.querySelector('.jdgm-sort-dropdown');
    const search = widget.querySelector('.jdgm-review-search');
    const judgeWidget = window.jdgm?.$ ? window.jdgm.$(widget) : null;
    const ratingFilter = judgeWidget?.data('filter-rating');
    const keyword = judgeWidget?.data('keyword');
    const customFormAnswer = judgeWidget?.data('cf-answer');
    return (!sort || sort.value === 'most-helpful') &&
      !ratingFilter && !keyword && !customFormAnswer && !(search?.value || '').trim();
  }

  function pageLink(page, className, label, text = '') {
    const currentClass = page === currentPage ? ' jdgm-curt' : '';
    return `<a class="jdgm-paginate__page ${className}${currentClass}" data-page="${page}" aria-label="${label}">${text}</a>`;
  }

  function paginationHtml(totalCount) {
    const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG.perPage));
    const page = Math.min(Math.max(1, currentPage), totalPages);
    const links = [];
    if (page > 1) {
      links.push(pageLink(1, 'jdgm-paginate__first-page', 'First page'));
      links.push(pageLink(page - 1, 'jdgm-paginate__prev-page', `Page ${page - 1}`));
    }
    let numericPages;
    if (totalPages <= 3) numericPages = Array.from({ length: totalPages }, (_, index) => index + 1);
    else if (page <= 2) numericPages = [1, 2, 3];
    else if (page >= totalPages - 1) numericPages = [totalPages - 2, totalPages - 1, totalPages];
    else numericPages = [page - 1, page, page + 1];
    for (const numericPage of numericPages) {
      links.push(pageLink(numericPage, '', `Page ${numericPage}`, String(numericPage)));
    }
    if (page < totalPages) {
      links.push(pageLink(page + 1, 'jdgm-paginate__next-page', `Page ${page + 1}`));
      links.push(pageLink(totalPages, 'jdgm-paginate__last-page', `Last page, page ${totalPages}`));
    }
    return links.join('');
  }

  function htmlToReviewNode(html) {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.querySelector('.jdgm-rev');
  }

  async function renderPage(page, focusFirstReview = false) {
    if (!isCustomOrderActive()) return false;
    const reviewsContainer = widget.querySelector('.jdgm-rev-widg__reviews');
    const pagination = widget.querySelector('.jdgm-paginate');
    if (!reviewsContainer || !pagination) return false;

    const sequence = ++renderSequence;
    reviewsContainer.setAttribute('aria-busy', 'true');
    reviewsContainer.style.opacity = '0.55';
    try {
      const result = await reviewsForCustomPage(page);
      if (sequence !== renderSequence || !isCustomOrderActive()) return false;
      if (result.reviews.length !== CONFIG.perPage && page < Math.ceil(result.totalCount / CONFIG.perPage)) {
        throw new Error(`Expected ${CONFIG.perPage} reviews for custom page ${page}, received ${result.reviews.length}`);
      }

      currentPage = page;
      const reviewNodes = result.reviews.map(htmlToReviewNode).filter(Boolean);
      reviewsContainer.replaceChildren(...reviewNodes);
      pagination.dataset.perPage = String(CONFIG.perPage);
      pagination.innerHTML = paginationHtml(result.totalCount);
      pagination.style.display = '';
      widget.dataset.reliefswebCuratedPage = String(page);
      widget.dataset.reliefswebCuratedVersion = CONFIG.version;

      if (window.jdgm?.customizeReviews) window.jdgm.customizeReviews();
      if (focusFirstReview) {
        const firstReview = reviewsContainer.querySelector('.jdgm-rev');
        if (firstReview) {
          firstReview.tabIndex = -1;
          firstReview.focus({ preventScroll: true });
          widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      return true;
    } catch (error) {
      console.error('[Reliefsweb curated Judge.me reviews]', error);
      return false;
    } finally {
      if (sequence === renderSequence) {
        reviewsContainer.removeAttribute('aria-busy');
        reviewsContainer.style.opacity = '';
      }
    }
  }

  function scheduleFirstPage() {
    window.setTimeout(() => {
      if (isCustomOrderActive()) renderPage(1, false);
    }, 700);
  }

  function onDocumentClick(event) {
    const paginationLink = event.target.closest?.('.jdgm-paginate__page');
    if (paginationLink && widget?.contains(paginationLink) && isCustomOrderActive()) {
      const page = Number(paginationLink.dataset.page);
      if (Number.isFinite(page) && page > 0 && page !== currentPage) {
        event.preventDefault();
        event.stopImmediatePropagation();
        renderPage(page, true);
      }
      return;
    }
    const clearFilter = event.target.closest?.('.jdgm-histogram__clear-filter, .jdgm-keywords__clear-filter');
    if (clearFilter && widget?.contains(clearFilter)) scheduleFirstPage();
  }

  function onDocumentChange(event) {
    if (!widget?.contains(event.target)) return;
    if (event.target.matches('.jdgm-sort-dropdown')) {
      if (event.target.value === 'most-helpful') scheduleFirstPage();
      else ++renderSequence;
    }
    if (event.target.matches('.jdgm-review-search') && !event.target.value.trim()) scheduleFirstPage();
  }

  function initialize() {
    if (initialized) return;
    widget = document.querySelector(`.jdgm-review-widget[data-id="${CONFIG.productId}"]`);
    if (!widget || !window.jdgm?.customizeReviews || !widget.querySelector('.jdgm-rev-widg__reviews')) {
      window.setTimeout(initialize, 250);
      return;
    }
    initialized = true;
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('change', onDocumentChange, false);
    renderPage(1, false);
  }

  window.__reliefswebCuratedReviews = {
    config: CONFIG,
    renderPage,
    naturalPositionForNonCuratedIndex,
    get currentPage() { return currentPage; },
    get active() { return isCustomOrderActive(); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
